import { useState, useEffect, useCallback } from 'react';

interface OfflineStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingChanges: number;
  lastSyncTime: string | null;
  hasUnsyncedData: boolean;
}

interface UseOfflineStatusReturn extends OfflineStatus {
  triggerSync: () => Promise<void>;
  refreshStatus: () => Promise<void>;
}

export const useOfflineStatus = (): UseOfflineStatusReturn => {
  const [status, setStatus] = useState<OfflineStatus>({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingChanges: 0,
    lastSyncTime: null,
    hasUnsyncedData: false,
  });

  const refreshStatus = useCallback(async () => {
    try {
      // With Supabase real-time, sync is automatic
      // We just need to track online/offline status
      setStatus(prev => ({
        ...prev,
        isSyncing: false,
        pendingChanges: 0, // Supabase handles this automatically
        hasUnsyncedData: false,
      }));
    } catch (error) {
      console.error('Failed to refresh offline status:', error);
    }
  }, []);

  const triggerSync = useCallback(async () => {
    if (!status.isOnline || status.isSyncing) return;
    
    try {
      // With Supabase real-time, sync happens automatically
      // This is just a placeholder for any manual sync needs
      console.log('Manual sync triggered - Supabase handles real-time sync automatically');
      await refreshStatus();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  }, [status.isOnline, status.isSyncing, refreshStatus]);

  useEffect(() => {
    const handleOnline = () => {
      setStatus(prev => ({ ...prev, isOnline: true }));
      // Trigger sync when coming back online
      if (status.pendingChanges > 0) {
        triggerSync();
      }
    };

    const handleOffline = () => {
      setStatus(prev => ({ ...prev, isOnline: false, isSyncing: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial status check
    refreshStatus();

    // Periodic status updates
    const interval = setInterval(refreshStatus, 10000); // Every 10 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [refreshStatus, triggerSync, status.pendingChanges]);

  return {
    ...status,
    triggerSync,
    refreshStatus,
  };
};

export default useOfflineStatus;
